/**
 * Hook for handling agent escalation events (notifications and choice requests)
 *
 * Usage:
 * ```tsx
 * const { notification, choiceRequest, dismissNotification, submitChoice } = useAgentEscalation();
 *
 * // Display notification banner
 * {notification && <AgentNotificationBanner {...notification} onDismiss={dismissNotification} />}
 *
 * // Display choice prompt
 * {choiceRequest && <AgentChoicePrompt {...choiceRequest} onSelect={submitChoice} />}
 * ```
 */

import { useState, useEffect, useCallback } from 'react';
import { messageService } from '../services/messageService';

// Define types locally to avoid build dependency issues
type AgentNotificationSeverity = 'info' | 'warning' | 'error';

interface AgentNotificationContext {
  attempts?: number;
  issue?: string;
  triedApproaches?: string[];
}

interface AgentChoiceOption {
  id: string;
  label: string;
  description: string;
}

interface AgentNotification {
  messageId: string;
  message: string;
  severity: AgentNotificationSeverity;
  context?: AgentNotificationContext;
  timestamp: string;
}

interface AgentChoiceRequest {
  messageId: string;
  requestId: string;
  question: string;
  context: string;
  options: AgentChoiceOption[];
  recommendation?: string;
  timestamp: string;
}

interface UseAgentEscalationReturn {
  /** Current notification to display (if any) */
  notification: AgentNotification | null;
  /** Current choice request to display (if any) */
  choiceRequest: AgentChoiceRequest | null;
  /** Dismiss the current notification */
  dismissNotification: () => void;
  /** Submit user's choice for a choice request */
  submitChoice: (requestId: string, optionId: string, customInput?: string) => Promise<void>;
  /** Clear all escalation state */
  clearAll: () => void;
}

export function useAgentEscalation(): UseAgentEscalationReturn {
  const [notification, setNotification] = useState<AgentNotification | null>(null);
  const [choiceRequest, setChoiceRequest] = useState<AgentChoiceRequest | null>(null);

  // Listen for agent notification events
  useEffect(() => {
    const handleNotification = (event: CustomEvent) => {
      const detail = event.detail;
      setNotification({
        messageId: detail.messageId,
        message: detail.message,
        severity: detail.severity,
        context: detail.context,
        timestamp: detail.timestamp,
      });

      // Auto-dismiss info notifications after 10 seconds
      if (detail.severity === 'info') {
        setTimeout(() => {
          setNotification((current) =>
            current?.timestamp === detail.timestamp ? null : current
          );
        }, 10000);
      }
    };

    window.addEventListener('agentNotification', handleNotification as EventListener);
    return () => {
      window.removeEventListener('agentNotification', handleNotification as EventListener);
    };
  }, []);

  // Listen for agent choice request events
  useEffect(() => {
    const handleChoiceRequest = (event: CustomEvent) => {
      const detail = event.detail;
      setChoiceRequest({
        messageId: detail.messageId,
        requestId: detail.requestId,
        question: detail.question,
        context: detail.context,
        options: detail.options,
        recommendation: detail.recommendation,
        timestamp: detail.timestamp,
      });
    };

    window.addEventListener('agentChoiceRequest', handleChoiceRequest as EventListener);
    return () => {
      window.removeEventListener('agentChoiceRequest', handleChoiceRequest as EventListener);
    };
  }, []);

  // Clear escalation state when stream completes
  useEffect(() => {
    const handleComplete = () => {
      // Keep notifications visible for a moment after completion
      setTimeout(() => {
        setNotification(null);
        setChoiceRequest(null);
      }, 2000);
    };

    window.addEventListener('aiResponseComplete', handleComplete);
    return () => {
      window.removeEventListener('aiResponseComplete', handleComplete);
    };
  }, []);

  const dismissNotification = useCallback(() => {
    setNotification(null);
  }, []);

  const submitChoice = useCallback(async (
    requestId: string,
    optionId: string,
    customInput?: string
  ) => {
    if (!choiceRequest || choiceRequest.requestId !== requestId) {
      console.warn('Choice request mismatch or already submitted');
      return;
    }

    // Find the selected option label for the message
    const selectedOption = choiceRequest.options.find(opt => opt.id === optionId);
    const choiceLabel = selectedOption?.label || optionId;

    // Send the user's choice as a follow-up message
    // The agent will receive this as the next user input
    const choiceMessage = customInput
      ? `I choose: ${choiceLabel}. ${customInput}`
      : `I choose: ${choiceLabel}`;

    try {
      // Send message to continue the conversation with user's choice
      // Extract conversationId from messageId (format: conversationId-timestamp or similar)
      const conversationId = choiceRequest.messageId.split('-').slice(0, 5).join('-'); // Handle UUID format

      await messageService.sendMessage({
        conversationId,
        content: choiceMessage,
      });

      // Clear the choice request
      setChoiceRequest(null);
    } catch (error) {
      console.error('Failed to submit choice:', error);
      throw error;
    }
  }, [choiceRequest]);

  const clearAll = useCallback(() => {
    setNotification(null);
    setChoiceRequest(null);
  }, []);

  return {
    notification,
    choiceRequest,
    dismissNotification,
    submitChoice,
    clearAll,
  };
}
